import type { RootState } from "@/store/store"
import type { Constituent, Customer, User } from "@/types"
import { setCustomer } from "@/components/customer/ducks/Actions"
import { NavigationActions } from "@/components/navigation/ducks/slice"
import { receiveCurrentConstituent } from "@/components/profile/admin/ducks/Actions"
import { receiveUpdateUser } from "@/components/profile/ducks/Actions"
import { refetchSearch } from "@/components/search/ducks/Actions"
import { customerSelectorFn } from "@/hooks/reduxHooks"
import { errHof, httpDelete, httpPost, httpPut } from "@/http"
import { createAppAsyncThunk } from "@/store/reduxUtils"

type BaseThunkOpts = {
  onSuccess?: () => void
  onError: (error: Error) => void
}

export const checkAuthorizeStripeThunk = createAppAsyncThunk(
  "donations/admin/checkAuthorizeStripe",
  async ({ onSuccess, onError }: BaseThunkOpts, { rejectWithValue, signal }) => {
    try {
      return await httpPost<{ url: string }>(
        "admin/authorize_stripe",
        {},
        {
          signal,
          success: onSuccess,
          error: errHof(onError, "checkAuthorizeStripe"),
        },
      )
    } catch (err) {
      return rejectWithValue(err)
    }
  },
  {
    condition: (_, { getState }) => {
      const customer = customerSelectorFn(getState() as RootState)

      // Ensure no dispatch if already connected
      if (customer?.stripeConnected) return false
    },
  },
)

export const delinkStripeThunk = createAppAsyncThunk(
  "donations/admin/delinkStripe",
  async ({ onSuccess, onError }: BaseThunkOpts, { rejectWithValue, signal, dispatch }) => {
    try {
      const res = await httpPost<{ customer: Customer }>(
        "admin/delink_stripe",
        {},
        { signal, success: onSuccess, error: errHof(onError, "delinkStripe") },
      )

      dispatch(setCustomer(res.customer))
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

type DeleteUserDonationParams = BaseThunkOpts & {
  donationId: string
}

export const deleteUserDonation = createAppAsyncThunk(
  "donations/admin/deleteUserDonation",
  async (
    { onSuccess, onError, donationId }: DeleteUserDonationParams,
    { rejectWithValue, signal, dispatch },
  ) => {
    try {
      const res = await httpDelete<{ currentUser: User }>(
        `admin/personalDonationManager/${donationId}`,
        {},
        {
          signal,
          success: onSuccess,
          error: errHof(onError, "deleteUserDonation"),
        },
      )

      if (res?.currentUser) {
        dispatch(receiveUpdateUser(res.currentUser))
      }

      return res
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

type EditUserDonationParams = BaseThunkOpts & {
  donationId: string
  params: {
    donationAmount: number
    donationDate: string
  }
}

export const editUserDonation = createAppAsyncThunk(
  "donations/admin/editUserDonation",
  async (
    { onSuccess, onError, donationId, params }: EditUserDonationParams,
    { rejectWithValue, signal, dispatch },
  ) => {
    try {
      const res = await httpPut<{ currentUser: User }, EditUserDonationParams["params"]>(
        `admin/personalDonationManager/${donationId}`,
        params,
        {
          signal,
          success: onSuccess,
          error: errHof(onError, "editUserDonation"),
        },
      )

      if (res?.currentUser) {
        dispatch(receiveUpdateUser(res.currentUser))
      }

      return res
    } catch (err) {
      rejectWithValue(err)
    }
  },
)

type AddUserDonationParams = BaseThunkOpts & {
  donationAmount: number
  donationDate: string
}

export const addUserDonation = createAppAsyncThunk(
  "donations/admin/addUserDonation",
  async (
    { onSuccess, onError, donationAmount, donationDate }: AddUserDonationParams,
    { rejectWithValue, signal, dispatch },
  ) => {
    try {
      const res = await httpPost<
        { currentUser: User },
        Omit<AddUserDonationParams, keyof BaseThunkOpts>
      >(
        "admin/personalDonationManager",
        { donationAmount, donationDate },
        {
          signal,
          success: onSuccess,
          error: errHof(onError, "addUserDonation"),
        },
      )

      if (res?.currentUser) {
        dispatch(receiveUpdateUser(res.currentUser))
      }

      return res
    } catch (err) {
      rejectWithValue(err)
    }
  },
)

type EditConstituentDonationParams = BaseThunkOpts & {
  constituentId: string
  donationId: string
  params: {
    donationAmount: number
    donationDate: string
  }
}

export const editConstituentDonation = createAppAsyncThunk(
  "donations/admin/editConstituentDonation",
  async (
    { onSuccess, onError, constituentId, donationId, params }: EditConstituentDonationParams,
    { rejectWithValue, signal, dispatch },
  ) => {
    try {
      const res = await httpPut<
        { constituent: Constituent },
        EditConstituentDonationParams["params"]
      >(`admin/donationManager/${constituentId}/${donationId}`, params, {
        signal,
        success: onSuccess,
        error: errHof(onError, "editConstituentDonation"),
      })

      if (res?.constituent) {
        dispatch(receiveCurrentConstituent(res.constituent))
        dispatch(refetchSearch(() => {}))
      }

      return res
    } catch (err) {
      rejectWithValue(err)
    }
  },
)

type DeleteConstituentDonationParams = BaseThunkOpts & {
  constituentId: string
  donationId: string
}

export const deleteConstituentDonation = createAppAsyncThunk(
  "donations/admin/deleteConstituentDonation",
  async (
    { onSuccess, onError, constituentId, donationId }: DeleteConstituentDonationParams,
    { rejectWithValue, signal, dispatch },
  ) => {
    try {
      const res = await httpDelete<{ constituent: Constituent }>(
        `admin/donationManager/${constituentId}/${donationId}`,
        {},
        {
          signal,
          success: onSuccess,
          error: errHof(onError, "deleteConstituentDonation"),
        },
      )

      if (res?.constituent) {
        dispatch(receiveCurrentConstituent(res.constituent))
        dispatch(refetchSearch(() => dispatch(NavigationActions.setSnackbar("Donation deleted"))))
      }

      return res
    } catch (err) {
      rejectWithValue(err)
    }
  },
)

type AddConstituentDonationParams = BaseThunkOpts & {
  constituentId: string
  donationAmount: number
  donationDate: string
}

export const addConstituentDonation = createAppAsyncThunk(
  "donations/admin/addConstituentDonation",
  async (
    {
      onSuccess,
      onError,
      constituentId,
      donationAmount,
      donationDate,
    }: AddConstituentDonationParams,
    { rejectWithValue, signal, dispatch },
  ) => {
    try {
      const res = await httpPost<
        { constituent: Constituent },
        Pick<AddConstituentDonationParams, "donationAmount" | "donationDate">
      >(
        `admin/donationManager/${constituentId}/`,
        { donationAmount, donationDate },
        {
          signal,
          success: onSuccess,
          error: errHof(onError, "addConstituentDonation"),
        },
      )

      if (res?.constituent) {
        dispatch(receiveCurrentConstituent(res.constituent))
        dispatch(refetchSearch(() => {}))
      }

      return res
    } catch (err) {
      rejectWithValue(err)
    }
  },
)
